import { Check, Copy } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface Props {
  readonly chart: string;
}

const PANZOOM_OPTIONS = {
  controlIconsEnabled: false,
  zoomEnabled: true,
  panEnabled: true,
  dblClickZoomEnabled: true,
  fit: true,
  center: true,
  minZoom: 0.2,
  maxZoom: 10,
} as const;

const COPY_TOAST_MS = 1500;

type Mode = "graph" | "code";

export function MermaidBlock({ chart }: Props) {
  const rawId = useId();
  const id = `mmd-${rawId.replace(/:/g, "-")}`;
  const ref = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<Mode>("graph");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "graph") return;
    let cancelled = false;
    let panZoom: { destroy: () => void } | null = null;

    (async () => {
      try {
        const [{ default: mermaid }, { default: svgPanZoom }] =
          await Promise.all([import("mermaid"), import("svg-pan-zoom")]);
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "dark",
        });
        const { svg } = await mermaid.render(id, chart);
        if (cancelled || ref.current === null) return;
        ref.current.innerHTML = svg;

        const svgEl = ref.current.querySelector("svg");
        if (svgEl !== null) {
          svgEl.style.width = "100%";
          svgEl.style.height = "100%";
          svgEl.style.maxWidth = "none";
          panZoom = svgPanZoom(svgEl, PANZOOM_OPTIONS);
        }
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Mermaid render failed");
      }
    })();

    return () => {
      cancelled = true;
      if (panZoom !== null) {
        try {
          panZoom.destroy();
        } catch {
          // SVG may already be detached by React — safe to ignore.
        }
      }
    };
  }, [chart, id, mode]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), COPY_TOAST_MS);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(chart);
      setCopied(true);
    } catch {
      // Clipboard blocked (insecure context / permission denied) — no-op.
    }
  };

  if (error !== null) {
    return (
      <pre className="not-prose my-3 overflow-auto rounded-lg bg-red-50 p-3 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-200">
        Mermaid error: {error}
        {"\n\n"}
        {chart}
      </pre>
    );
  }

  const toggleBase =
    "px-2 py-1 transition text-[10px] font-bold uppercase tracking-wide";
  const toggleActive = "bg-black/10 text-black dark:bg-white/15 dark:text-white";
  const toggleInactive =
    "text-black/60 hover:text-black dark:text-white/60 dark:hover:text-white";

  return (
    <div className="not-prose relative my-3 h-[400px] w-full overflow-hidden rounded-lg bg-black/5 dark:bg-white/5">
      <div className="absolute right-2 top-2 z-10 flex rounded-md border border-black/10 bg-white/80 backdrop-blur dark:border-white/15 dark:bg-black/40">
        <button
          type="button"
          onClick={() => setMode("graph")}
          className={cn(
            toggleBase,
            "rounded-l-md",
            mode === "graph" ? toggleActive : toggleInactive,
          )}
        >
          Graph
        </button>
        <button
          type="button"
          onClick={() => setMode("code")}
          className={cn(
            toggleBase,
            "rounded-r-md",
            mode === "code" ? toggleActive : toggleInactive,
          )}
        >
          Code
        </button>
      </div>

      {mode === "graph" ? (
        <div
          ref={ref}
          className="h-full w-full cursor-grab active:cursor-grabbing"
        />
      ) : (
        <>
          <pre className="h-full w-full overflow-auto p-3 pr-4 pt-10 font-mono text-xs leading-relaxed text-black/85 dark:text-white/85">
            {chart}
          </pre>
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy mermaid source"
            className="absolute bottom-2 right-2 z-10 flex items-center gap-1 rounded-md border border-black/10 bg-white/80 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-black/70 backdrop-blur transition hover:bg-white dark:border-white/15 dark:bg-black/40 dark:text-white/70 dark:hover:bg-black/60"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3" /> Copied
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" /> Copy
              </>
            )}
          </button>
        </>
      )}
    </div>
  );
}
